import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Dialog, DialogContent, styled } from "@mui/material";

import { Passenger } from "@/data/models/passenger.model";
import { ReservationFormInput, reservationFormSchema, emptyReservationFormValues, type ReservationFormValues } from "./schema/reservationForm.schema";

import { ReservationFormMachinesProvider } from "./ReservationFormMachinesContext";

import ReservationDialogHeader from "@/components/home/reservations/ReservationDialogHeader";
import { ReservationTripLegForm } from "./ReservationTripLegForm";
import { ReservationMemberForm } from "./ReservationMemberForm";
import { ReservationSummary } from "./ReservationSummary";
import OverrideDialog from "../home/reservations/OverrideDialog";
import ConfirmationDialog from "../ConfirmationDialog";
import { ServicePlan } from "@/data/models/service-plan.model";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DateTime } from "luxon";

const PREFIX = 'CreateReservationDialog';

const classes = {
  content: `${PREFIX}-content`,
  pages: `${PREFIX}-pages`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    padding: 0,
    overflow: 'visible',
  },
  [`& .${classes.pages}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    maxWidth: '1152px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(18),
      paddingRight: theme.spacing(18),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
    },
  },
}));

interface CreateReservationDialogProps {
  open: boolean;
  onClose: () => void;
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
}

const CreateReservationDialog = ({
  open,
  onClose,
  passenger,
  servicePlans,
}: CreateReservationDialogProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [isOverridden, setIsOverridden] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const form = useForm<ReservationFormInput, unknown, ReservationFormValues>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: emptyReservationFormValues,
    mode: 'onSubmit',
  });

  const pickUpTime = form.watch('trips.0.pickupDateTime');
  const appointmentDate = form.watch('trips.0.appointmentDate');
  const tripLegServiceDate =
    (pickUpTime instanceof DateTime && pickUpTime.isValid
      ? pickUpTime.toISODate()
      : null) ??
    (appointmentDate instanceof DateTime && appointmentDate.isValid
      ? appointmentDate.toISODate()
      : null) ??
    (typeof appointmentDate === 'string' && appointmentDate.trim()
      ? appointmentDate
      : null) ??
    undefined;
  const overridableServicePlans = servicePlans.filter(
    ({ effectiveStartDate, effectiveEndDate }) =>
      tripLegServiceDate != null &&
      effectiveStartDate <= tripLegServiceDate &&
      (!effectiveEndDate || tripLegServiceDate <= effectiveEndDate),
  );

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleReservationSubmit = async (_values: ReservationFormValues) => {
    setActiveStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);
  };

  const handleClose = () => {
    onClose();
  };

  const handleOverrideOpen = () => {
    setOverrideDialogOpen(true);
  };

  const handleSaveAsDraft = () => {
    form.handleSubmit(handleReservationSubmit)();
  };

  const handleOverrideClose = (data: any | null) => {
    setOverrideDialogOpen(false);
  };

  const handleConfirmationDialogClose = () => {
    setConfirmDialogOpen(false);
  };


  return (
    <StyledDialog open={open} onClose={onClose} fullScreen>
      <ReservationDialogHeader
        passengerName={
          passenger ? `${passenger.firstName} ${passenger.lastName}` : ''
        }
        title={t('reservation.createTrip')}
        steps={[t('reservation.tripDetailsStep'), t('reservation.member'), t('reservation.summary')]}
        activeStep={activeStep}
        isValid={true}
        step={handleStep}
        open={onClose}
      />
      <DialogContent className={classes.content}>
        <Box className={classes.pages}>
          <ReservationFormMachinesProvider>
            {activeStep === 0 && (
              <ReservationTripLegForm
                form={form}
                onSubmit={handleReservationSubmit}
                servicePlans={servicePlans}
                handleSaveAsDraft={handleSaveAsDraft}
                handleOverrideOpen={handleOverrideOpen}
              />
            )}
            {activeStep === 1 && <ReservationMemberForm />}
            {activeStep === 2 && (
              <ReservationSummary goBack={handlePrevious} />
            )}
          </ReservationFormMachinesProvider>
        </Box>
      </DialogContent>
      <OverrideDialog
        open={overrideDialogOpen}
        servicePlans={overridableServicePlans}
        close={handleOverrideClose}
      />
      <ConfirmationDialog
        title={t('common.discardChanges')}
        message={t('common.possibleDataLoss')}
        open={confirmDialogOpen}
        positiveAction={handleClose}
        negativeAction={handleConfirmationDialogClose}
      />
    </StyledDialog>
  );
};

export { CreateReservationDialog };